import ReceiptIcon from "@mui/icons-material/ReceiptLongRounded";
import { Avatar, Stack, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { use } from "react";
import StatusChip from "@entrilia/shared/components/StatusChip";
import { makeModeAwareAccent } from "@entrilia/shared/utilities/colorHelper";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { InvoiceLayoutBanner, LayoutBanner, ObjectFieldValues } from "../../api/types/objectTypes";
import EntitySection from "./EntitySection";
import { getLookupReferenceForFieldId } from "./helpers";
import ObjectCardContext from "./ObjectCardContext";

interface Props<TBanner extends LayoutBanner = LayoutBanner> {
  banner: TBanner;
  objectData: ObjectFieldValues;
}

const InvoiceBannerSection = ({ banner, objectData }: Props<InvoiceLayoutBanner>) => {
  const source = use(ObjectCardContext)?.source;
  const titleLookupRef = getLookupReferenceForFieldId(banner.fieldMappings.TitleField, objectData);
  const title = titleLookupRef
    ? titleLookupRef.primaryFieldValue
    : stringifyValue(objectData.fieldValues[banner.fieldMappings.TitleField]);
  const dueDate = convertISODate(stringifyValue(objectData.fieldValues[banner.fieldMappings.DueDateField]));
  const amount = objectData.fieldValues[banner.fieldMappings.AmountField];
  const currencyCode = stringifyValue(objectData.fieldValues[banner.fieldMappings.CurrencyField]);
  const details = source?.details;

  const summary = (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Stack direction="row" spacing={1.5}>
        <Avatar variant="rounded">
          <ReceiptIcon color="action" />
        </Avatar>

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="subtitle1">{title}</Typography>
            {source && (
              <StatusChip
                label={source.label}
                tooltipText={source.description}
                color={(t) => makeModeAwareAccent(t, blue)}
                height="smaller"
              />
            )}
          </Stack>
          {dueDate && (
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption" color="textSecondary">
                Payment Due:
              </Typography>
              <Typography variant="caption">{dueDate}</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      {typeof amount === "number" && (
        <Typography variant="h5">
          {formatNumber(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currencyCode}
        </Typography>
      )}
    </Stack>
  );

  if (!details) {
    return summary;
  }

  return (
    <Stack spacing={1.5}>
      {summary}
      <Stack spacing={0.5} sx={{ pl: 6.5 }}>
        {details}
      </Stack>
    </Stack>
  );
};

const ObjectBannerSection = ({ banner, objectData }: Props) => {
  return (
    <EntitySection sx={{ p: 2 }} withTopBorder>
      {banner.type === "Invoice" && <InvoiceBannerSection banner={banner} objectData={objectData} />}
    </EntitySection>
  );
};

export default ObjectBannerSection;
